import React, { useEffect, useState } from 'react';
import { getClientContactConsent, submitClientContactConsent } from '../../../api/v2/client_contact_consents';

import styles from './index.module.css';

const ClientContactConsent = ({ match }) => {
  const { token } = match.params;
  const [status, setStatus] = useState('loading');
  const [payload, setPayload] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    let cancelled = false;

    getClientContactConsent(token)
      .then((data) => {
        if (cancelled) return;
        setPayload(data);
        setStatus(data.status || 'pending');
      })
      .catch((error) => {
        if (cancelled) return;
        setPayload(error.data || null);
        setStatus(error.data?.status || 'not_found');
        setErrorMessage(error.message || 'This consent link is invalid.');
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleResponse = async (response) => {
    setSubmitting(true);
    setErrorMessage('');
    try {
      const data = await submitClientContactConsent(token, response);
      setPayload(data);
      setStatus(data.status || 'consumed');
    } catch (error) {
      setPayload(error.data || null);
      setStatus(error.data?.status || 'error');
      setErrorMessage(error.message || 'Unable to save your response.');
    } finally {
      setSubmitting(false);
    }
  };

  if (status === 'loading') {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <div className={styles.loading}>
            <div className={styles.skeletonTitle} />
            <div className={styles.skeletonRow} />
            <div className={styles.skeletonRow} style={{ width: '60%' }} />
          </div>
        </div>
      </div>
    );
  }

  if (status === 'expired_resent') {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <h1 className={styles.title}>New link sent</h1>
          <p className={styles.description}>
            {payload?.message ||
              'This link expired. We sent you a new link by text and email. Please use the newest message.'}
          </p>
        </div>
      </div>
    );
  }

  if (status === 'consumed') {
    const consented = payload?.response === true;
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <div className={styles.successIcon}>
            <svg viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
              <circle cx='12' cy='12' r='11' stroke='currentColor' strokeWidth='2' />
              <path
                d='M7 12.5L10.5 16L17 9'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
          <h1 className={styles.title}>{consented ? 'Consent recorded' : 'Response recorded'}</h1>
          <p className={styles.description}>
            {payload?.message ||
              (consented
                ? 'Thank you. Your consent has been recorded.'
                : 'Thank you. Your response has been recorded.')}
          </p>
        </div>
      </div>
    );
  }

  if (status === 'not_found' || status === 'error') {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <h1 className={styles.title}>Link unavailable</h1>
          <p className={styles.description}>{errorMessage || 'This consent link is invalid or no longer available.'}</p>
        </div>
      </div>
    );
  }

  const accountName = payload?.account_name;

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <p className={styles.brand}>PatientPartner</p>
        <h1 className={styles.title}>May we share your contact information?</h1>
        <p className={styles.description}>
          {accountName
            ? `${accountName} may need to follow up with you about a reported interaction.`
            : 'A partner may need to follow up with you about a reported interaction.'}{' '}
          Please choose whether we may share your contact information with them.
        </p>
        {errorMessage && <div className={styles.errorBox}>{errorMessage}</div>}
        <div className={styles.actions}>
          <button
            type='button'
            className={`${styles.btn} ${styles.btnYes}`}
            onClick={() => handleResponse(true)}
            disabled={submitting}
          >
            {submitting ? 'Saving…' : 'Yes, you may contact me'}
          </button>
          <button
            type='button'
            className={`${styles.btn} ${styles.btnNo}`}
            onClick={() => handleResponse(false)}
            disabled={submitting}
          >
            No
          </button>
        </div>
        <p className={styles.footnote}>This link expires in 24 hours.</p>
      </div>
    </div>
  );
};

export default ClientContactConsent;
